import {
  ENDPOINTS,
  BUSINESS_STATUSES,
  PAYMENT_SOURCES,
  PAYMENT_METHOD,
  BILLING_METHODS,
  BOOKSY_SUBSCRIPTION_STATUS,
  WEBVIEW_TYPES,
  INCOME_TYPES,
  APP_TYPE,
  BILLING_PAYMENT_PROCESSORS,
  STRIPE_FLOW,
} from '@/lib/consts.ts'

import { includesRoute, routesMatch } from '@/components/settings/subscription/helpers.js'
import { useLogger } from '@booksy-com/vue-tools'

import { useCore } from '@/composables/core'

import { isEmpty, path } from 'ramda'

import type { Module } from 'vuex'

import type { RootState, SubscriptionState } from '@/types/store'

const logger = useLogger('store.subscription')

const INITIAL_STATE = {
  subscriptionBilling: null,
  subscriptionLegacy: null,
  smsManagement: null,
  billingGlobalSettings: null,
  billingSettings: null,
  navisionSettings: null,
  invoiceDetails: {},
  offer: null,
  requestSubmitted: false,
  nextPayment: null,
  paymentMethods: null,
  overduePayments: {
    total: null,
    details: null,
    boost: null,
    subscription: null,
  },
}

const TRIAL_STATUSES = [
  BUSINESS_STATUSES.TRIAL,
  BUSINESS_STATUSES.TRIAL_END,
  BUSINESS_STATUSES.TRIAL_BLOCKED,
]

const subscriptionModule = {
  namespaced: true,

  /*!
   *
   * state
   *
   */
  state: INITIAL_STATE,

  /*!
   *
   * actions
   *
   */
  actions: {
    reset ({ commit }) {
      commit('setSubscriptionBilling', { subscriptionBilling: null })
      commit('setSubscriptionLegacy', { subscriptionLegacy: null })
      commit('setOffer', { offer: null })
    },

    resetOffer ({ commit }) {
      commit('setOffer', { offer: null })
    },

    loadNavisionSettings ({ commit, dispatch, rootState }) {
      return dispatch('api/call', {
        url: ENDPOINTS.BILLING_NAVISION_SETTINGS,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setNavisionSettings', {
            navisionSettings: data,
          })
          return true
        })
    },

    loadSubscriptionBilling ({ commit, dispatch, rootState }) {
      return dispatch('api/call', {
        url: ENDPOINTS.BILLING_SUBSCRIPTION_DETAILS,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setSubscriptionBilling', {
            subscriptionBilling: data,
          })

          // status: success
          return true
        })
    },

    loadSubscriptionLegacy ({ commit, dispatch, rootState }) {
      return dispatch('api/call', {
        method: 'GET',
        url: ENDPOINTS.SUBSCRIPTIONS,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        params: {
          braintree_token: false,
          braintree_payment: true,
        },
      }, { root: true })
        .then(async ({ data }) => {
          commit('setSubscriptionLegacy', {
            subscriptionLegacy: data,
          })
        })
    },

    getSubscriptionBuyerData ({ dispatch, rootState }) {
      return dispatch('api/call', {
        method: 'GET',
        url: ENDPOINTS.SUBSCRIPTION_BUYER,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true })
    },

    saveSubscriptionBuyerData ({ dispatch, rootState }, { data, dryRun }: { data?: unknown; dryRun?: boolean } = {}) {
      const params: Record<string, unknown> = {}

      if (dryRun) {
        params.dry_run = 1
      }

      return dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.SUBSCRIPTION_BUYER,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data,
        params,
      }, { root: true })
    },

    updateSubscriptionBuyerData ({ dispatch, rootState }, { data, dryRun }: { data?: unknown; dryRun?: boolean } = {}) {
      const params: Record<string, unknown> = {}

      if (dryRun) {
        params.dry_run = 1
      }

      return dispatch('api/call', {
        method: 'PUT',
        url: ENDPOINTS.SUBSCRIPTION_BUYER,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data,
        params,
      }, { root: true })
    },

    getSubscriptionBuyerDataV2 ({ commit, dispatch, rootState }) {
      return dispatch('api/call', {
        method: 'GET',
        url: ENDPOINTS.SUBSCRIPTION_BUYER_V2,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(response => {
          const { data } = response
          commit('setInvoiceDetails', {
            invoiceDetails: data,
          })
          return response
        })
    },

    saveSubscriptionBuyerDataV2 ({ dispatch, rootState }, data) {
      return dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.SUBSCRIPTION_BUYER_V2,
        urlParams: { businessId: rootState.business.business.id },
        data,
      }, { root: true })
    },

    updateSubscriptionBuyerDataV2 ({ dispatch, rootState }, data) {
      return dispatch('api/call', {
        method: 'PATCH',
        url: ENDPOINTS.SUBSCRIPTION_BUYER_V2,
        urlParams: { businessId: rootState.business.business.id },
        data,
      }, { root: true })
    },

    loadGlobalBillingSettings ({ commit, dispatch, state, rootState }, { useCache = true } = {}) {
      if (useCache && state.billingGlobalSettings) {
        return Promise.resolve(state.billingGlobalSettings)
      }

      return dispatch('api/call', {
        url: ENDPOINTS.BILLING_GLOBAL_SETTINGS,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setBillingGlobalSettings', {
            billingGlobalSettings: data,
          })
          return data
        })
    },

    loadBillingSettings ({ commit, dispatch, rootState, state }) {
      return dispatch('api/call', {
        url: ENDPOINTS.BILLING_BUSINESS_SETTINGS,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setBillingSettings', {
            billingSettings: data,
          })

          return data
        })
    },

    loadAllBillingSettings ({ commit, dispatch, rootState }, { useCache = true } = {}) {
      return Promise.all([
        dispatch('loadGlobalBillingSettings', { useCache }),
        dispatch('loadBillingSettings'),
        dispatch('loadNavisionSettings'),
      ])
    },

    loadSmsManagement ({ dispatch, rootState, commit }) {
      return dispatch('api/call', {
        method: 'GET',
        url: ENDPOINTS.SMS_MANAGEMENT,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true }).then(({ data }) => {
        commit('setSmsManagement', data)
      })
    },

    loadOffer ({ commit, dispatch, rootState }) {
      return dispatch('api/call', {
        url: ENDPOINTS.BILLING_OFFER,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setOffer', {
            offer: data,
          })

          return true
        })
    },

    fetchRequest ({ commit, dispatch, rootState }) {
      return dispatch('api/call', {
        url: ENDPOINTS.OFFLINE_SUBSCRIPTION_REQUEST,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setRequestSubmitted', { requestSubmitted: data.already_requested })
        })
    },

    submitRequest ({ commit, dispatch, rootState }) {
      return dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.OFFLINE_SUBSCRIPTION_REQUEST,
        urlParams: { businessId: rootState.business.business.id },
      }, { root: true })
        .then(() => {
          commit('setRequestSubmitted', { requestSubmitted: true })
        })
    },

    updateSmsManagement ({ dispatch, rootState }, payload) {
      return dispatch('api/call', {
        method: 'PUT',
        url: ENDPOINTS.SMS_MANAGEMENT,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data: payload,
      }, { root: true })
        .then(() => {
          dispatch('loadSmsManagement')
        })
    },

    addDiscount ({ dispatch, rootState }, payload) {
      return dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.DISCOUNT_CODE,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data: {
          is_purchase: payload?.isPurchase || false,
          discount_code: payload?.code,
        },
      }, { root: true })
    },

    async paymentIntentInit ({ dispatch, rootState }, {
      offer_id,
      product_ids,
    }) {
      const response = await dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.BILLING_SUBSCRIPTION_PURCHASE,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data: {
          offer_id,
          product_ids,
        },
      }, { root: true })

      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    async purchaseWithPaymentIntent ({ dispatch, rootState }, {
      paymentIntentId,
    }) {
      const response = await dispatch('api/call', {
        method: 'PUT',
        url: ENDPOINTS.BILLING_SUBSCRIPTION_PURCHASE,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data: {
          transaction_id: paymentIntentId,
        },
      }, { root: true })

      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    async setupIntentInit ({ dispatch, rootState }) {
      const response = await dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.STRIPE_PAYMENT_METHOD,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true })

      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    async saveStripePaymentMethod ({ dispatch, rootState }, { setupIntentId, paymentMethodId, attemptReference }) {
      const response = await dispatch('api/call', {
        method: 'PUT',
        url: ENDPOINTS.STRIPE_PAYMENT_METHOD,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data: {
          setup_id: setupIntentId,
          payment_id: paymentMethodId,
          default: true,
        },
        ...(attemptReference && {
          headers: {
            'X-Nethone-Attempt-Reference': attemptReference,
          },
        }),
      }, { root: true })

      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    async saveBraintreePaymentMethod ({ dispatch, rootState }, { data }) {
      const response = await dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.BILLING_INFO,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data,
      }, { root: true })

      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    async subscribe ({ dispatch, rootState }, {
      offer_id,
      product_ids,
      agreements,
    }) {
      const response = await dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.BILLING_SUBSCRIPTION,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data: {
          offer_id,
          product_ids,
          ...(agreements || {}),
        },
      }, { root: true })

      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    async retryCharge ({ dispatch, rootState, getters }) {
      const response = await dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.BILLING_RETRY_CHARGE,
        urlParams: {
          businessId: rootState.business.business.id,
        },
        data: {
          subscription_id: getters.subscription?.id,
        },
      }, { root: true })

      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    loadPaymentMethods ({ dispatch, rootState }) {
      return rootState.subscription.billingSettings?.payment_processor === BILLING_PAYMENT_PROCESSORS.BRAINTREE
        ? dispatch('loadLegacyPaymentMethods')
        : dispatch('loadStripePaymentMethods')
    },

    loadLegacyPaymentMethods ({ dispatch, rootState, commit }) {
      if (rootState.business.business.status !== BUSINESS_STATUSES.TRIAL) {
        return dispatch('api/call', {
          method: 'GET',
          url: ENDPOINTS.BILLING_PAYMENT_METHOD,
          urlParams: {
            businessId: rootState.business.business.id,
          },
        }, { root: true }).then(({ data }) => {
          commit('setPaymentMethods', {
            paymentMethods: [data],
          })
          return { data: [data] }
        })
      } else {
        return { data: [] }
      }
    },

    loadStripePaymentMethods ({ dispatch, rootState, commit }) {
      return dispatch('api/call', {
        method: 'GET',
        url: ENDPOINTS.BILLING_PAYMENT_METHODS,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true }).then(({ data }) => {
        commit('setPaymentMethods', {
          paymentMethods: data,
        })
        return { data }
      })
    },

    setStripeDefaultPaymentMethod ({ dispatch, rootState }, paymentMethodId) {
      return dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.BILLING_PAYMENT_SET_DEFAULT,
        urlParams: {
          businessId: rootState.business.business.id,
          paymentMethodId,
        },
      }, { root: true })
    },

    deleteStripePaymentMethod ({ dispatch, rootState }, paymentMethodId) {
      return dispatch('api/call', {
        method: 'DELETE',
        url: ENDPOINTS.BILLING_PAYMENT_METHOD_DELETE,
        urlParams: {
          businessId: rootState.business.business.id,
          paymentMethodId,
        },
      }, { root: true })
    },

    changeCardNetwork ({ dispatch, rootState }, {
      paymentMethodId,
      networkCode,
    }) {
      if (!networkCode) return Promise.reject(new Error('no network code'))

      return dispatch('api/call', {
        method: 'PATCH',
        url: ENDPOINTS.BILLING_PAYMENT_METHOD_UPDATE,
        urlParams: {
          businessId: rootState.business.business.id,
          paymentMethodId,
        },
        data: {
          preferred_network: networkCode,
        },
      }, { root: true })
    },

    async loadBillingNextPayment ({ commit, dispatch, rootState }) {
      const { data: nextPayment } = await dispatch('api/call', {
        url: ENDPOINTS.BILLING_NEXT_PAYMENT,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true })

      commit('setNextPayment', { nextPayment })

      return true
    },

    async loadNextPayment ({ commit, dispatch, rootState }) {
      const { data: nextPayment } = await dispatch('api/call', {
        url: ENDPOINTS.BILLING_NEXT_PAYMENT,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true })

      commit('setNextPayment', { nextPayment })

      return true
    },

    async loadOverduePaymentsTotal ({ commit, dispatch, rootState }) {
      try {
        const { data } = await dispatch('api/call', {
          url: ENDPOINTS.BILLING_OVERDUE_TOTAL,
          urlParams: {
            businessId: rootState.business.business.id,
          },
        }, { root: true })

        commit('setOverduePaymentsTotal', data)

        return data
      } catch ({ response }) {
        logger.error(response)
      }
    },

    async loadOverduePaymentsDetails ({ commit, dispatch, rootState }) {
      try {
        const { data } = await dispatch('api/call', {
          url: ENDPOINTS.BILLING_SUBSCRIPTION_OVERDUE_DETAILS,
          urlParams: {
            businessId: rootState.business.business.id,
          },
        }, { root: true })

        commit('setOverduePaymentsDetails', data)
        return data
      } catch ({ response }) {
        logger.error(response)
      }
    },

    async loadOverduePaymentsBoost ({ commit, dispatch, rootState }) {
      const { data } = await dispatch('api/call', {
        url: ENDPOINTS.BILLING_OVERDUE_BOOST,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true })

      commit('setOverduePaymentsBoost', data)
      return data
    },

    reportAbortedOfflineMigration ({ dispatch, rootState }) {
      return dispatch('api/call', {
        method: 'PUT',
        url: ENDPOINTS.OFFLINE_MIGRATION_POPUP,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true })
        .then(() => {
          dispatch('loadBillingSettings')
        })
    },

    async boostRetryCharge ({ dispatch, rootState }) {
      const response = await dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.BOOST_RETRY_CHARGE,
        urlParams: {
          businessId: rootState.business.business.id,
        },
      }, { root: true })
      return getTaskStatus(dispatch, rootState, response.data.task_id)
    },

    async payOverdue ({ dispatch, state, rootState, getters }) {
      const { t } = useCore()

      let subscriptionSuccess
      let boostSuccess

      if (state.overduePayments.total.subscription_overdue_amount > 0) {
        try {
          await dispatch('retryCharge')
          subscriptionSuccess = true
        } catch {
          subscriptionSuccess = false
        }
      }

      if (state.overduePayments.total.boost_overdue_amount > 0) {
        try {
          await dispatch('boostRetryCharge')
          boostSuccess = true
        } catch {
          boostSuccess = false
        }
      }

      if (subscriptionSuccess && boostSuccess) {
        return Promise.resolve(
          t('components.subscription.overdue-payments.retry-payment-messages.all-success')
        )
      }

      if (!subscriptionSuccess && boostSuccess) {
        return Promise.resolve(
          t('components.subscription.overdue-payments.retry-payment-messages.boost-success-only')
        )
      }

      if (subscriptionSuccess && !boostSuccess) {
        return Promise.resolve(
          t('components.subscription.overdue-payments.retry-payment-messages.subscription-success-only')
        )
      }

      if (!subscriptionSuccess && !boostSuccess) {
        return Promise.reject(
          t('components.subscription.overdue-payments.retry-payment-messages.none-success')
        )
      }
    },
  },

  /*!
   *
   * mutations
   *
   */
  mutations: {
    setSubscriptionBilling (state, { subscriptionBilling }) {
      state.subscriptionBilling = subscriptionBilling
    },

    setSubscriptionLegacy (state, { subscriptionLegacy }) {
      state.subscriptionLegacy = subscriptionLegacy
    },

    setBillingGlobalSettings (state, { billingGlobalSettings }) {
      state.billingGlobalSettings = billingGlobalSettings
    },

    setBillingSettings (state, { billingSettings }) {
      state.billingSettings = billingSettings
    },

    setNavisionSettings (state, { navisionSettings }) {
      state.navisionSettings = navisionSettings
    },
    setInvoiceDetails (state, { invoiceDetails }) {
      state.invoiceDetails = invoiceDetails
    },

    setOffer (state, { offer }) {
      state.offer = offer
    },

    resetOffer (state) {
      state.offer = null
    },

    setSmsManagement (state, payload) {
      state.smsManagement = payload
    },

    setRequestSubmitted (state, { requestSubmitted }) {
      state.requestSubmitted = requestSubmitted
    },

    setNextPayment (state, { nextPayment }) {
      state.nextPayment = nextPayment
    },

    setPaymentMethods (state, { paymentMethods }) {
      state.paymentMethods = paymentMethods
    },

    setBoost (state, { cost, gross_cost, tax }) {
      if (state.nextPayment) {
        state.nextPayment.boost.cost = cost
        state.nextPayment.boost.gross_cost = gross_cost
        state.nextPayment.boost.tax = tax
      } else {
        state.nextPayment = {
          boost: {
            cost,
            gross_cost,
            tax,
          },
        }
      }
    },

    setOverduePaymentsTotal (state, data) {
      state.overduePayments.total = data
    },

    setOverduePaymentsDetails (state, data) {
      state.overduePayments.details = data
    },

    setOverduePaymentsBoost (state, data) {
      state.overduePayments.boost = data
    },
  },

  /*
   *
   * getters
   *
   */
  getters: {
    subscription: (state, getters) => {
      return getters.useLegacySubscription
        ? state.subscriptionLegacy
        : state.subscriptionBilling
    },

    activeLegacySubscription: (state) => {
      return state.subscriptionLegacy?.subscriptions.find(
        subscription => subscription.active === true)
    },

    subscriptionBilling: (state, getters) => {
      return state.subscriptionBilling
    },

    hasSubscription: (state, getters) => {
      return !isEmpty(getters.subscription)
    },

    hasActiveLegacySubscription: (state, getters) =>
      [
        BUSINESS_STATUSES.PAID_ACTIVE,
        BUSINESS_STATUSES.ACTIVE_OVERDUE,
      ].includes(state.subscriptionLegacy?.status),

    hasActiveBillingSubscription: (state) =>
      [
        BOOKSY_SUBSCRIPTION_STATUS.ACTIVE,
        BOOKSY_SUBSCRIPTION_STATUS.PENDING,
      ].includes(state.subscriptionBilling?.status),

    hasActiveMobileSubscription: (state, getters, rootState) =>
      [
        PAYMENT_SOURCES.ITUNES,
        PAYMENT_SOURCES.PLAY,
      ].includes(rootState.business.business.payment_source)
      && [
        BUSINESS_STATUSES.PAID_ACTIVE,
        BUSINESS_STATUSES.ACTIVE_OVERDUE,
      ].includes(rootState.business.business.status),

    hasActiveSubscription: (state, getters) =>
      getters.hasActiveLegacySubscription
      || getters.hasActiveBillingSubscription
      || getters.hasActiveMobileSubscription,

    item: (state, getters) => {
      if (getters.useLegacySubscription) {
        return path(['product'], getters.activeLegacySubscription) || {}
      }

      return path(['products', 'SA', 0], state.subscriptionBilling) || {}
    },

    name: (state, getters) => {
      if (getters.useLegacySubscription) {
        return path(['product', 'name'], getters.activeLegacySubscription) || ''
      }

      return path(['products', 'SA', 0, 'name'], state.subscriptionBilling) || ''
    },

    status: (state, getters) => {
      if (getters.useLegacySubscription) {
        return state.subscriptionLegacy?.status
      }

      return state.subscriptionBilling && state.subscriptionBilling.status
    },

    totalPrice: (state, getters) => {
      if (getters.useLegacySubscription) {
        return Number(
          path(['product', 'price_amount'], getters.activeLegacySubscription)
        )
      }

      return Number(
        path(['products', 'SA', 0, 'total_final_price_gross'], state.subscriptionBilling)
      )
    },

    nextBillingDate: (state, getters) => {
      if (getters.useLegacySubscription) {
        return path(['billing_cycle', 'end'], getters.activeLegacySubscription)
      }

      return state.subscriptionBilling.next_billing_date
    },

    isLongSubscription: (state) => {
      return state.subscriptionBilling.is_long_subscription
    },

    subscriptionPeriod: (state) => {
      return state.subscriptionBilling?.subscription_period
    },

    staffersCount: (state, getters) => {
      return getters.useLegacySubscription
        ? null
        : state.subscriptionBilling.staff_count
    },

    staffDiscountedPrice: state => {
      return path(['products', 'SA', 0, 'staff_discounted_price_gross'], state.subscriptionBilling) || '0'
    },

    totalSmsAmountLabel: (state) => {
      if (state.smsManagement) {
        const countFree = path(['statistics', 0, 'count_free'], state.smsManagement) || 0
        const limitFree = path(['statistics', 0, 'limit_free'], state.smsManagement) || 0

        return `${countFree}/${limitFree}`
      }

      return path(['products', 'SA', 0, 'total_sms_amount'], state.subscriptionBilling)?.toString() || '0'
    },

    totalPaidSmsAmountLabel: (state) => {
      const countPaid = path(['statistics', 0, 'count_paid'], state.smsManagement) || 0
      const limitPaid = path(['statistics', 0, 'limit_paid'], state.smsManagement) || 0

      return `${countPaid}/${limitPaid}`
    },

    billingMethod: (state, getters, rootState) => {
      if (!rootState.business.business) {
        return BILLING_METHODS.UNAVAILABLE
      }

      const { webView, appType } = rootState.app
      const { has_access } = rootState.business.billingAccess || {}
      const { status, payment_source, has_new_billing } = rootState.business.business

      const isPaymentSourceMobile = [PAYMENT_SOURCES.PLAY, PAYMENT_SOURCES.ITUNES].includes(payment_source)
      const isPaymentSourceUnknownOnMobilePlatform = payment_source === PAYMENT_SOURCES.UNKNOWN && [WEBVIEW_TYPES.ANDROID, WEBVIEW_TYPES.IOS].includes(webView)
      const isBusinessTrialOnPWA = (status === BUSINESS_STATUSES.TRIAL && ![APP_TYPE.MOBILE, APP_TYPE.TABLET].includes(appType))

      if (isPaymentSourceMobile || isPaymentSourceUnknownOnMobilePlatform) {
        if (isBusinessTrialOnPWA || status === BUSINESS_STATUSES.TRIAL_BLOCKED) {
          return BILLING_METHODS.UNAVAILABLE
        }

        if (payment_source === PAYMENT_SOURCES.PLAY && webView === WEBVIEW_TYPES.IOS) {
          return BILLING_METHODS.ITUNES
        }

        if (payment_source === PAYMENT_SOURCES.PLAY || webView === WEBVIEW_TYPES.ANDROID) {
          return BILLING_METHODS.GOOGLE_PLAY
        }

        return BILLING_METHODS.ITUNES
      }

      if (payment_source === PAYMENT_SOURCES.OFFLINE) {
        return BILLING_METHODS.OFFLINE
      }

      // Every payment source with blocked/churn status except "booksy billing and offline"
      if (payment_source !== PAYMENT_SOURCES.BOOKSY_BILLING
        && [
          BUSINESS_STATUSES.BLOCKED_OVERDUE,
          BUSINESS_STATUSES.CHURNED,
        ].includes(status)
      ) {
        return BILLING_METHODS.UNAVAILABLE
      }

      if (has_new_billing) {
        return BILLING_METHODS.BOOKSY_BILLING
      }

      // additional check based on state.billingAccess
      if (payment_source === PAYMENT_SOURCES.UNKNOWN && has_access) {
        return BILLING_METHODS.BOOKSY_BILLING
      }

      // webView === false; standalone desktop or mobile internet browser
      // default view for "UNKNOWN" payment source
      if (!webView && payment_source === PAYMENT_SOURCES.UNKNOWN) {
        return BILLING_METHODS.UNAVAILABLE
      }

      return BILLING_METHODS.UNAVAILABLE
    },

    offlinePaymentMethod: (state, getters) => BILLING_METHODS.OFFLINE === getters.billingMethod,

    useLegacySubscription: (state, getters) => {
      return [
        BILLING_METHODS.OFFLINE,
        BILLING_METHODS.GOOGLE_PLAY,
        BILLING_METHODS.ITUNES,
      ].includes(getters.billingMethod)
    },

    requestSubmitted: state => state.requestSubmitted,

    currency: (state) => {
      return path(['products', 'SA', 0, 'currency'], state.subscriptionBilling)
        || path(['products', 'SA', 0, 'currency'], state.offer)
    },

    mobilePaymentMethod: (state, getters) =>
      [
        BILLING_METHODS.GOOGLE_PLAY,
        BILLING_METHODS.ITUNES,
      ].includes(getters.billingMethod),

    offerItem: state => {
      return state.offer?.products?.SA?.[0]
    },

    offerPeriod: state => {
      return state.offer?.subscription_period
    },

    offerItemMarketingSmsCount: (state, getters) => {
      return getters?.offerItem?.total_sms_amount
    },

    offerTotalPricePrimary: (state, getters, rootState) => {
      const { expose_gross_price } = rootState.routingConfig.config
      const { offerItem } = getters

      return expose_gross_price ? offerItem?.total_price : offerItem?.total_price_gross
    },

    offerTotalPriceSecondary: (state, getters, rootState) => {
      const { expose_gross_price } = rootState.routingConfig.config
      const { offerItem } = getters

      return expose_gross_price ? offerItem?.total_price_gross : offerItem?.total_price
    },

    offerTotalFinalPricePrimary: (state, getters, rootState) => {
      const { expose_gross_price } = rootState.routingConfig.config
      const { offerItem } = getters

      return expose_gross_price ? offerItem?.total_final_price : offerItem?.total_final_price_gross
    },

    offerTotalFinalPriceSecondary: (state, getters, rootState) => {
      const { expose_gross_price } = rootState.routingConfig.config
      const { offerItem } = getters

      return expose_gross_price ? offerItem?.total_final_price_gross : offerItem?.total_final_price
    },

    offerStaffFullPricePrimary: (state, getters, rootState) => {
      const { expose_gross_price } = rootState.routingConfig.config
      const { offerItem } = getters

      return expose_gross_price ? offerItem?.staff_full_price : offerItem?.staff_full_price_gross
    },

    offerStaffFullPriceSecondary: (state, getters, rootState) => {
      const { expose_gross_price } = rootState.routingConfig.config
      const { offerItem } = getters

      return expose_gross_price ? offerItem?.staff_full_price_gross : offerItem?.staff_full_price
    },

    primaryIncomeType: (state, getters, rootState) => {
      return rootState.routingConfig.config.expose_gross_price ? INCOME_TYPES.NET : INCOME_TYPES.GROSS
    },

    secondaryIncomeType: (state, getters, rootState) => {
      return rootState.routingConfig.config.expose_gross_price ? INCOME_TYPES.GROSS : INCOME_TYPES.NET
    },

    stateFieldEnabled: () => {
      // disabled for all countries
      // https://redmine.booksy.pm/issues/75284
      return false
    },

    isRouteAvailable: (state, getters, rootState) => {
      return (routeName) => {
        if (!getters.invoiceDetailsVisible
          && routesMatch('billing-details\\.invoice-details', routeName)
        ) {
          return false
        }

        const BLOCKED_OVERDUE_ALLOWED_ROUTES = [
          '', // subscription index route
          'your-subscription',
          'billing-details',
          'billing-details\\.billing-history',

          'billing-details\\.your-billing',
          'billing-details\\.your-billing\\.billing-history',
          'billing-details\\.your-billing\\.overdue-payments',

          'payment-method',
          'payment-method-change',
          'payment-method-info',
          'billing-details\\.invoice-details',
        ]

        if (
          [
            BUSINESS_STATUSES.BLOCKED_OVERDUE,
            BUSINESS_STATUSES.CHURNED,
          ].includes(rootState.business.business.status)
          && !includesRoute(BLOCKED_OVERDUE_ALLOWED_ROUTES, routeName)
        ) {
          return false
        }

        // disable routes for "OFFLINE" payment source
        const OFFLINE_EXCLUDED_ROUTES = [
          'billing-details\\.your-billing\\.next-payment',
          'payment-method',
          'payment-method-change',
          'payment-method-info',
        ]
        if (getters.offlinePaymentMethod && includesRoute(OFFLINE_EXCLUDED_ROUTES, routeName)) {
          return false
        }

        if (routesMatch('billing-details.billing-history', routeName) && !getters.offlinePaymentMethod) {
          return false
        }

        // disable `billing-details` route for "OFFLINE" payment source in specified countries
        const COUNTRIES_TO_EXCLUDE = [
          'mx',
          'za',
          'br',
        ]
        if (getters.offlinePaymentMethod
          && routesMatch('billing-details', routeName)
          && COUNTRIES_TO_EXCLUDE.includes(rootState.app.country)) {
          return false
        }

        if (getters.offlinePaymentMethod
          && rootState.routingConfig.config.hide_invoice_details_from_offline_merchants
          && routesMatch('billing-details\\.invoice-details', routeName)) {
          return false
        }

        // https://redmine.booksy.pm/issues/85098#note-4
        // disable payment method settings when no active subscription present
        if (routesMatch('payment-method.*', routeName)
          && isEmpty(getters.subscription)) {
          return false
        }

        return true
      }
    },

    supportedPaymentMethods: (state, getters, rootState, rootGetters) => {
      const paymentProcessor = getters.switchToStripe
        ? BILLING_PAYMENT_PROCESSORS.STRIPE
        : state.billingSettings?.payment_processor

      if (paymentProcessor) {
        let paymentMethods = state
          .billingGlobalSettings
          ?.payment_methods_by_processor[paymentProcessor]
          .map(payment_method => payment_method[1])

        // remove VENMO payment method for all non-mobile app instances
        if (!rootGetters['app/isTablet']
          && !rootGetters['app/isIOS']
          && !rootGetters['app/isAndroid']
        ) {
          paymentMethods = paymentMethods?.filter(pm => pm !== PAYMENT_METHOD.VENMO) || []
        }

        return paymentMethods
      }

      return []
    },

    isMoreThanOneSupportedPaymentMethod: (state, getters) => {
      return getters.supportedPaymentMethods?.length > 1
    },

    isApplicationBlocked: (state, getters, rootState) => [
      BUSINESS_STATUSES.TRIAL_BLOCKED,
      BUSINESS_STATUSES.BLOCKED_OVERDUE,
      BUSINESS_STATUSES.CHURNED,
    ].includes(rootState.business.business.status),

    trialMode: (state, getters, rootState) => {
      const subscription = getters.subscription

      if (getters.billingMethod === BILLING_METHODS.BOOKSY_BILLING) {
        return !(subscription && BOOKSY_SUBSCRIPTION_STATUS.ACTIVE === subscription.status)
        && TRIAL_STATUSES.includes(rootState.business.business.status)
      }
      return TRIAL_STATUSES.includes(rootState.business.business.status)
    },

    nextPayment: (state) => state.nextPayment,

    grossPricingEnabled: (state) => state.billingSettings?.gross_pricing_enabled || false,

    availableTaxGroups: (state, getters, rootState) => {
      return state.billingGlobalSettings.available_tax_groups
    },

    defaultTaxGroup: (state, getters, rootState) => {
      return state.billingSettings.default_tax_group
    },

    taxGroupsEnabled: (state, getters, rootState) => {
      return !!state?.billingGlobalSettings?.available_tax_groups?.length
    },

    useVatRegisteredFlag: state => state?.navisionSettings?.use_vat_registered_flag,

    switchToStripe: (state, getters, rootState) => {
      return rootState.routingConfig.config.switch_to_stripe
    },

    invoicingDetailsEditingEnabled: (state, getters, rootState) => {
      return rootState.subscription.billingSettings?.invoice_details_edit_enabled
    },

    // used to display and editing "invoice details" in settings and during purchase subscription process
    invoiceDetailsVisible: (state, getters, rootState) => {
      return getters.offlinePaymentMethod ? !state.navisionSettings?.hide_invoice_details_from_offline_merchants : true
    },

    paymentProcessor: state => state.billingSettings?.payment_processor,
    paymentMethods: state => state.paymentMethods,

    defaultPaymentMethod: state => {
      return state.paymentMethods
        && state.paymentMethods.length
        && state.paymentMethods.find(paymentMethod => paymentMethod.default)
    },

    offlineMigrationAllowed: state => state.billingSettings?.offline_migration_allowed,

    isOfflineBoostMigration: state => state.billingSettings?.boost_offline_migration_required,

    allowOnlyExistingZipcodes: state => state.billingGlobalSettings?.allow_only_existing_zipcodes,

    purchaseFlow: (state) => {
      if (state.billingGlobalSettings?.authorize_with_transaction_allowed) {
        return state.billingSettings?.purchase_flow
      }

      return STRIPE_FLOW.AUTHORIZE_WITH_CARD
    },

    isBooksyBilling: (state, getters) => {
      return getters.billingMethod === BILLING_METHODS.BOOKSY_BILLING
    },

    isDiscountAvailable: (state, getters) => {
      // disable discounts for `subscriptions longer than month`
      if (state.offer?.subscription_period > 1) { // when no subscription purchased
        return false
      }
      if (getters.subscription?.subscription_period > 1) { // when purchased
        return false
      }

      return getters.isBooksyBilling
    },

    offlineHardSwitchEnabled: (state, getters) => {
      return getters.offlineMigrationAllowed
    },

    offlineHardSwitchDiscount: (state) => {
      return state.billingSettings?.offline_migration_discount_offer_display
        && state.billingSettings?.offline_migration_discount_offer_percent
        ? `${state.billingSettings?.offline_migration_discount_offer_percent}%`
        : null
    },

    offlineHardSwitchDiscountPeriod: (state) => {
      return state.billingSettings?.offline_migration_discount_offer_period
    },

    offlineHardSwitchBlockDaysCountdown: (state) => {
      return state.billingSettings?.offline_migration_lockout_counter
    },

    offlineHardSwitchDate: () => {
      return '2023-07-01' // hardcoded business date
    },

    getOverduePaymentsNotificationCount: (state) => {
      let notificationsCount = 0

      if (Number(state.overduePayments?.total?.subscription_overdue_amount) > 0
        && state.overduePayments?.details?.product_charges
      ) {
        notificationsCount += 1
      }

      if (
        Number(state.overduePayments?.total?.boost_overdue_amount) > 0
        && state.overduePayments?.boost?.appointments?.length
      ) {
        notificationsCount += state.overduePayments?.boost?.appointments?.length
      }

      return notificationsCount
    },
  },
} as Module<SubscriptionState, RootState>

export default subscriptionModule

const getTaskStatus = (dispatch, rootState, taskId) => {
  return new Promise((resolve, reject) => {
    dispatch('api/call', {
      method: 'GET',
      url: ENDPOINTS.BILLING_SUBSCRIPTION_TASK,
      urlParams: {
        businessId: rootState.business.business.id,
        taskId: taskId,
      },
    }, { root: true })
      .then(response => {
        const { data: { status } } = response
        if (status === 'SUCCESS') {
          resolve(response)
        } else if (status === 'ERROR') {

          reject({
            response,
            taskId,
          })
        } else {
          setTimeout(() => {
            resolve(getTaskStatus(dispatch, rootState, taskId))
          }, 2000)
        }
      }).catch(error => {
        logger.error(error)

        reject({ response: {
          error,
          taskId,
        } })
      })
  })
}
